import React, { FC, useCallback, useContext, useMemo } from 'react';
import { DialogContainer } from './_Container/DialogContainer';
import { DialogHeader } from './_Header/DialogHeader';
import { DialogContext } from './context';
import { IDialogContext } from './models';
import { BreakpointsContext } from '../../providers/Breakpoints';
import { Modal } from '@src/core/Modal';
import { IRequiredIdentify } from '@src/models/analytics';
import { IDefaultComponentProps, TModalModeProps } from '@src/models/props';
import { Unit } from '@src/core/Unit';
import { IModalProps } from '@src/core/Modal/models';

interface INestedComponents {
  Header: typeof DialogHeader;
}

interface IProps
  extends IDefaultComponentProps,
    IRequiredIdentify,
    TModalModeProps,
    Pick<IModalProps, 'importanceLevel'> {
  /** Функция вызова при клике на задний фон */
  onBackdropClick?: (event: React.MouseEvent<HTMLDivElement>) => void;

  /** Статус диалога */
  open?: boolean;

  /** Без крестика */
  disableCloseBtn?: boolean;

  /** Не давать закрывать по "Esc" */
  disableCloseOnEscapeKeyDown?: boolean;

  /** Функция закрытия диалога */
  onClose: () => void;

  /** Классы для заднего фона */
  backdropClasses?: string;
}

/** Компонент для отображения диалогового окна. */
export const Dialog: FC<IProps> & INestedComponents = ({
  id,
  children,
  disableCloseBtn,
  disableCloseOnEscapeKeyDown,
  className,
  style,
  open,
  mode = 'default',
  backdropClasses,
  importanceLevel,
  onBackdropClick,
  onClose,
}) => {
  const { isMobile } = useContext(BreakpointsContext);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const contextValue = useMemo<IDialogContext>(
    () => ({
      open,
      disableCloseBtn,
      isMobile,
      id,
      onClose: handleClose,
    }),
    [disableCloseBtn, handleClose, id, isMobile, open],
  );

  const currentMode: TModalModeProps['mode'] = isMobile ? 'default' : mode;

  if (!open) {
    return null;
  }

  return (
    <DialogContext.Provider value={contextValue}>
      <Modal
        open
        onClose={handleClose}
        onBackdropClick={onBackdropClick}
        backdropClasses={backdropClasses}
        importanceLevel={importanceLevel}
        mode={currentMode}
      >
        <Unit id={id}>
          <DialogContainer
            mode={currentMode}
            className={className}
            style={style}
            disableCloseOnEscapeKeyDown={disableCloseOnEscapeKeyDown}
          >
            {children}
          </DialogContainer>
        </Unit>
      </Modal>
    </DialogContext.Provider>
  );
};

Dialog.Header = DialogHeader;
